import { AbstractControl, FormArray, Validators } from '@angular/forms';
import { TravelType } from '@app/enums/travel-type.enum';
import { Caregiver } from '@models/patient';
import { PersistentFormControl, PersistentFormGroup } from '@utility/persistent-forms';
import { FormGroupValueOf } from '@utility/utility';
import {
    disallowedValueValidator,
    equalOrMoreThanTodayOnlyIfCRCDateValidator,
    maxLengthValidator,
    notWhitespaceValidator,
} from '@utility/utility.validators';
import { Moment } from 'moment';

export class SingleFlightFormGroup extends PersistentFormGroup<{
    travelType: PersistentFormControl<TravelType | null>;
    airportOrigin: PersistentFormControl<string | null>;
    airportDestination: PersistentFormControl<string | null>;
    departureDate: PersistentFormControl<Moment | null>;
    returnDate: PersistentFormControl<Moment | null>;
    specialRequests: PersistentFormControl<string | null>;
    id: PersistentFormControl<number | null>;
}> {
    public static create(value: FormGroupValueOf<SingleFlightFormGroup>, noTravelAllowed: boolean) {
        const formGroup = new SingleFlightFormGroup(
            {
                travelType: new PersistentFormControl(value.travelType, [
                    disallowedValueValidator(TravelType.NO_FLIGHT, noTravelAllowed, 'Value is required'),
                ]),
                airportOrigin: new PersistentFormControl(value.airportOrigin, [
                    Validators.required,
                    notWhitespaceValidator(),
                    maxLengthValidator(70),
                ]),
                airportDestination: new PersistentFormControl(value.airportDestination, [
                    Validators.required,
                    notWhitespaceValidator(),
                    maxLengthValidator(70),
                ]),
                departureDate: new PersistentFormControl(value.departureDate, [
                    Validators.required,
                    equalOrMoreThanTodayOnlyIfCRCDateValidator,
                ]),
                returnDate: new PersistentFormControl(value.returnDate, [
                    Validators.required,
                    equalOrMoreThanTodayOnlyIfCRCDateValidator,
                ]),
                specialRequests: new PersistentFormControl(value.specialRequests, [
                    notWhitespaceValidator(),
                    maxLengthValidator(1000),
                ]),
                id: new PersistentFormControl(value.id),
            },
            [
                (control: AbstractControl<SingleFlightFormGroup['value']>) => {
                    const value = control.value;
                    if (!value.departureDate || !value.returnDate) return null;
                    return value.departureDate <= value.returnDate
                        ? null
                        : {
                              invalidDates: 'Return date cannot be before departure date.',
                          };
                },
            ]
        );
        return formGroup;
    }
}

export class CaregiverFlightFormGroup extends PersistentFormGroup<{
    details: PersistentFormGroup<{
        bookIdenticalTrip: PersistentFormControl<boolean | null>;
        flightDetails: SingleFlightFormGroup;
    }>;
    caregiver: PersistentFormControl<Caregiver>; // helper property, not visible in UI
}> {
    public static create(value: FormGroupValueOf<CaregiverFlightFormGroup>): CaregiverFlightFormGroup {
        return new CaregiverFlightFormGroup({
            caregiver: new PersistentFormControl(value.caregiver),
            details: new PersistentFormGroup({
                flightDetails: SingleFlightFormGroup.create(value.details.flightDetails, true),
                bookIdenticalTrip: new PersistentFormControl(value.details.bookIdenticalTrip),
            }),
        });
    }
}

export class FlightRequestDetailsFormGroup extends PersistentFormGroup<{
    patientFlightDetails: SingleFlightFormGroup;
    caregivers: FormArray<CaregiverFlightFormGroup>;
}> {
    public static create(value: FormGroupValueOf<FlightRequestDetailsFormGroup>): FlightRequestDetailsFormGroup {
        return new FlightRequestDetailsFormGroup({
            patientFlightDetails: SingleFlightFormGroup.create(value.patientFlightDetails, false),
            caregivers: new FormArray(
                value.caregivers.map((caregiverDetails) => CaregiverFlightFormGroup.create(caregiverDetails))
            ),
        });
    }
}
